import React, { useRef, useCallback, useState } from 'react'
import Button from 'components/Common/Button'
import { Grid, Box } from '@mui/material'
import GeneratedAvatar from 'components/Common/GeneratedAvatar'
import api from 'api'
import routes from '../../api/routes'
import { useDispatch } from 'react-redux'
import { Formik, Field, Form } from 'formik'
import { useIsMountedRef } from 'hooks'
import { AccountFormField } from 'components/Common/AccountFormLayout'
import TextField from 'components/Common/TextField'
import { useOnClickOutside } from 'hooks'
import AddressFields from 'components/Common/AddressFields'

import { store } from 'reduxStore'
import ZendeskLink from 'components/Common/ZendeskLink'
import { PRIMARY_BUTTON, SECONDARY_BUTTON, DISABLED_BUTTON } from 'themes/theme'
import * as yup from 'yup'

const schema = yup.object({
  zip: yup
    .string()
    .matches(/^\d{5}(?:[-\s]\d{4})?$/, 'Please, fill Zip Code in correct format')
})

const sx = {
  gridItem: {
    padding: '0 30px',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '1px',
      height: '100%',
      backgroundColor: 'rgb(217,220,220)',
    },
    '&:last-child': {
      paddingRight: 0,
      '&::before': {
        content: 'none',
      },
    },
  },
  actionButton: {
    fontSize: '0.75rem',
    whiteSpace: 'nowrap',
    width: '165px',
    marginTop: '10px',
  },
}

const sxBusinessTitles = {
  categories: {
    position: 'absolute',
    top: 80,
    width: '100%',
    padding: '8px',
    zIndex: 1000,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    overflowY: 'auto',
    maxHeight: 220,
    fontSize: '14px',
    marginTop: '10px',
    boxShadow: '4px 4px 15px rgba(10, 120, 205, 0.08)',
    borderRadius: '5px',
  },
  span: {
    color: '#66668F',
    padding: '10px',
    cursor: 'pointer',
    display: 'block',
    borderRadius: '5px',
    '&:hover': {
      backgroundColor: '#F4F4F7',
    },
  },
  closed: {
    display: 'none',
  },
}

const BusinessTitle = ({ categories, setValue, dropToDefault, style, lastSelectedValue, setLastValue }) => {
  const ref = useRef(null)
  useOnClickOutside(ref, () => {
    dropToDefault([])
    setValue('businessTitle', lastSelectedValue)
  })

  const action = item => {
    dropToDefault([])
    setLastValue && setLastValue(item)
    setValue('businessTitle', item)
  }

  if (categories.length === 0) return null

  return (
    <Box ref={ref} sx={sxBusinessTitles.categories} style={style}>
      {categories.map(item => (
        <Box component={'span'} onClick={() => action(item)} sx={sxBusinessTitles.span} key={item}>
          {item}
        </Box>
      ))}
    </Box>
  )
}

const MyProfile = props => {
  const user = store.getState().userReducer.user
  const [userPic, updatePic] = useState(
    `${process.env['REACT_APP_BACKEND_BASE_AUTH_URL']}/static/images/users/${user.avatarFileId}`,
  )

  const [businessPhotoId, setPhotoId] = useState(null)
  const dispatch = useDispatch()
  const inputFile = useRef()
  const formik = useRef(null)
  const isMountedRef = useIsMountedRef()
  const [lastTitle, setLastTitle] = useState('')
  const [businessTitles, setBusinessTitles] = useState([])

  const getCompanyTitles = async search => {
    const { titles } = await api.getCompanyTitles(search)
    setBusinessTitles(titles)
  }

  const fetchChartData = async () => {
    await api.getDashboardInfo()
  }

  const initialFormValue = {
    firstName: user?.firstName,
    lastName: user?.lastName,
    businessTitle: user?.businessTitle,
    avatarUrl: user?.avatarUrl,
    address: user?.address,
    city: user?.city,
    state: user?.state,
    zip: user?.zip,
    avatarFileId: user?.avatarFileId,
  }

  const onFormSubmit = useCallback(
    async (values, formikHelpers) => {
      formikHelpers.setSubmitting(true)
      try {
        //@ts-ignore
        delete values.accountType
        const user = await api.updateMyAccountProfile({ ...values, prefName: '', country: '' })
        dispatch({ type: 'userUpdated', user })
        await api.getUser().then(async user => {
          dispatch({ type: 'userLoaded', user })
        })
        fetchChartData()
      } catch (error) {
      } finally {
        if (isMountedRef.current) {
          formikHelpers.setSubmitting(false)
        }
      }
    },
    [dispatch, isMountedRef],
  )

  const onAvatarAddClick = () => {
    inputFile.current.click()
  }

  const onFileFieldChange = useCallback(
    async event => {
      if (event.currentTarget.files) {
        const file = event.currentTarget.files[0]
        event.target.value = ''
        const formData = new FormData()
        formData.append('file', file)

        try {
          const bphotoId = await api.updateAvatar(formData, routes.userAvatar)
          if (user !== null) {
            const newUser = {
              ...user,
              avatarFileId: bphotoId.fileId,
              avatarUrl: `${process.env['REACT_APP_BACKEND_BASE_AUTH_URL']}/static/images/users/${bphotoId.fileId}`,
            }
            dispatch({ type: 'userUpdated', user: newUser })
            fetchChartData()
          }
          setPhotoId(bphotoId.fileId)
        } catch (e) {
          console.log(e)
        }
      }
    },
    [dispatch, user],
  )

  const onAvatarDeleteClick = useCallback(async () => {
    try {
      await api.deleteAvatar(routes.userAvatar)
      if (user !== null) {
        const newUser = { ...user, avatarFileId: '', avatarUrl: '' }
        dispatch({ type: 'userUpdated', user: newUser })
        fetchChartData()
      }
      updatePic(null)
      setPhotoId(null)
    } catch (e) {
      console.log(e)
    }
  }, [dispatch, user])

  if (user === null) return null

  return (
    <Formik
      innerRef={formik}
      onSubmit={onFormSubmit}
      validationSchema={schema}
      initialValues={initialFormValue}
      enableReinitialize
    >
      {({ dirty, isSubmitting, values, setFieldValue }) => {
        return (
          <Form>
            <Grid container justifyContent="flex-end" marginTop="4%" className="customAuthInput">
              <Grid item xs={12} md={4.5} sx={sx.gridItem}>
                <AccountFormField>
                  <Field name="firstName">
                    {({ field }) => <TextField {...field} label="First Name" fullWidth borderLess />}
                  </Field>
                </AccountFormField>
                <AccountFormField>
                  <Field name="lastName">
                    {({ field }) => <TextField {...field} label="Last Name" fullWidth borderLess />}
                  </Field>
                </AccountFormField>
                <Box position="relative">
                  <AccountFormField>
                    <Field name="businessTitle">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          isSelect
                          label="Business Title"
                          fullWidth
                          borderLess
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          onClickSelect={() => {
                            getCompanyTitles('')
                          }}
                          onChange={e => {
                            if (!e) {
                              getCompanyTitles('')
                              setFieldValue('businessTitle', '')
                            }
                            getCompanyTitles(e.target.value)
                            setFieldValue('businessTitle', e.target.value)
                          }}
                          onFocus={() => {
                            getCompanyTitles('')
                            // setFieldValue('businessTitle', '')
                          }}
                        />
                      )}
                    </Field>
                  </AccountFormField>
                  <BusinessTitle
                    setValue={setFieldValue}
                    dropToDefault={setBusinessTitles}
                    categories={businessTitles}
                    lastSelectedValue={lastTitle}
                    setLastValue={setLastTitle}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={4.5} sx={sx.gridItem}>
                <Box mb={5}>
                  <AddressFields setFieldValue={setFieldValue} initValues={user} borderLess />
                </Box>
                <Box sx={sx.fieldsWrap}>
                  <AccountFormField>
                    <Field name="city">
                      {({ field }) => <TextField {...field} label="City" fullWidth borderLess />}
                    </Field>
                  </AccountFormField>
                  <AccountFormField>
                    <Field name="state">
                      {({ field }) => <TextField {...field} label="State" fullWidth borderLess />}
                    </Field>
                  </AccountFormField>
                  <AccountFormField>
                    <Field name="zip">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          label="Zip Code"
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          fullWidth
                          borderLess
                        />
                      )}
                    </Field>
                  </AccountFormField>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.5} sx={sx.gridItem} className={`d-flex align-items-center flex-column ps-0`}>
                <input
                  type="file"
                  accept=".jpeg, .jpg, .png"
                  id="file"
                  ref={inputFile}
                  style={{ display: 'none' }}
                  onChange={onFileFieldChange}
                />
                {businessPhotoId !== null ? (
                  <img
                    src={process.env['REACT_APP_BACKEND_BASE_AUTH_URL'] + '/static/images/users/' + businessPhotoId}
                    style={{ height: '152px', width: '152px', borderRadius: '50%' }}
                    alt="user avatar updated"
                  />
                ) : null}
                {!businessPhotoId && (
                  <GeneratedAvatar
                    width={140}
                    height={140}
                    fontSize={60}
                    fontWeight={600}
                    src={userPic}
                    alt={`${user.firstName} ${user.lastName}`}
                    text={user.firstName}
                  />
                )}
                <Box
                  sx={{
                    padding: '30px',
                  }}
                >
                  <Button onClick={onAvatarAddClick} fullWidth>
                    Add Photo
                  </Button>
                  <Button
                    type={SECONDARY_BUTTON}
                    fullWidth
                    negative
                    onClick={onAvatarDeleteClick}
                    sx={{
                      marginTop: 2,
                    }}
                  >
                    Delete Photo
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={1}></Grid>
              <Grid item xs={10} marginBottom={4}>
                <Button
                  submit
                  loading={isSubmitting}
                  type={!dirty ? DISABLED_BUTTON : PRIMARY_BUTTON}
                  sx={{ width: { xs: '50%', sm: '50%', md: '20%', lg: '15%', xl: '15%' }, marginTop: '20px' }}
                >
                  Update
                </Button>
              </Grid>
            </Grid>
            <ZendeskLink />
          </Form>
        )
      }}
    </Formik>
  )
}

export default MyProfile
